/**
 * Zugriff auf Folienmaster, Layouts, Design und Dokumenteigenschaften.
 *
 * Der Folienmaster mit seinen Layouts lässt sich schon ab PowerPointApi 1.3
 * lesen – das ist der wichtigste Teil, denn daran hängt der rote Faden des
 * Kurses: Jede Folie trägt ein Layout, jedes Layout gehört zu einem Master.
 *
 * Farben des Designs und der Folienhintergrund kommen erst mit 1.10 dazu, die
 * Dokumenteigenschaften mit 1.7. Jede Funktion gibt `null` zurück, wenn sie in
 * dieser PowerPoint-Version nichts liefern kann.
 */

import { powerPointApi, supportsPowerPointApi } from "./capabilities";
import { isPowerPointAvailable, PowerPointUnavailableError } from "./presentation";

export interface LayoutInfo {
  id: string;
  name: string;
  /** "Title", "TitleOnly", "SectionHeader", "TwoObjects", … `null` vor 1.8. */
  type: string | null;
}

export interface SlideMasterInfo {
  id: string;
  name: string;
  layouts: LayoutInfo[];
}

/**
 * Alle Folienmaster der Präsentation mit ihren Layouts.
 * `null`, wenn PowerPointApi 1.3 fehlt.
 */
export async function readSlideMasters(): Promise<SlideMasterInfo[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.shapes)) return null;

  const withType = supportsPowerPointApi(powerPointApi.layoutType);

  try {
    return await PowerPoint.run(async (context) => {
      const masters = context.presentation.slideMasters;
      masters.load("items/id,items/name");
      await context.sync();

      const layoutCollections = masters.items.map((master) => {
        const layouts = master.layouts;
        layouts.load(withType ? "items/id,items/name,items/type" : "items/id,items/name");
        return layouts;
      });
      await context.sync();

      return masters.items.map((master, index) => ({
        id: typeof master.id === "string" ? master.id : String(index),
        name: typeof master.name === "string" ? master.name : "",
        layouts: layoutCollections[index].items.map((layout, layoutIndex) => ({
          id: typeof layout.id === "string" ? layout.id : String(layoutIndex),
          name: typeof layout.name === "string" ? layout.name : "",
          type: withType && typeof layout.type === "string" ? layout.type : null,
        })),
      }));
    });
  } catch {
    return null;
  }
}

export interface LayoutShapeInfo {
  /** Layout, zu dem die Form gehört. */
  layoutId: string;
  name: string | null;
  /** Platzhalterart, sofern lesbar (ab 1.8). */
  placeholderType: string | null;
  /** Sollposition und Sollgröße in Punkt. */
  left: number | null;
  top: number | null;
  width: number | null;
  height: number | null;
}

/**
 * Die Platzhalter aller Layouts mit ihrer vorgesehenen Position.
 *
 * Nur damit lässt sich beantworten, ob jemand einen Platzhalter auf der Folie
 * von Hand verschoben hat – die Folie selbst weiß davon nichts, sie kennt nur
 * ihre aktuelle Position. `null`, wenn PowerPointApi 1.4 fehlt; ohne Position
 * und Größe wäre der Vergleich sinnlos.
 */
export async function readLayoutShapes(): Promise<LayoutShapeInfo[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.shapeDetails)) return null;

  const withPlaceholder = supportsPowerPointApi(powerPointApi.placeholders);

  try {
    return await PowerPoint.run(async (context) => {
      const masters = context.presentation.slideMasters;
      masters.load("items/id");
      await context.sync();

      const layoutCollections = masters.items.map((master) => {
        const layouts = master.layouts;
        layouts.load("items/id");
        return layouts;
      });
      await context.sync();

      const layouts = layoutCollections.flatMap((collection) => collection.items);
      const shapeCollections = layouts.map((layout) => {
        const shapes = layout.shapes;
        shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
        return shapes;
      });
      await context.sync();

      const placeholderFormats = new Map<string, PowerPoint.PlaceholderFormat>();
      if (withPlaceholder) {
        shapeCollections.forEach((shapes, index) => {
          for (const shape of shapes.items) {
            if (shape.type !== "Placeholder") continue;
            const format = shape.placeholderFormat;
            format.load("type");
            placeholderFormats.set(`${index}:${shape.id}`, format);
          }
        });
        try {
          await context.sync();
        } catch {
          placeholderFormats.clear();
        }
      }

      const asNumber = (value: unknown) =>
        typeof value === "number" && Number.isFinite(value) ? value : null;

      return shapeCollections.flatMap((shapes, index) => {
        const layoutId = typeof layouts[index].id === "string" ? layouts[index].id : String(index);
        return shapes.items.map((shape) => {
          const format = placeholderFormats.get(`${index}:${shape.id}`);
          let placeholderType: string | null = null;
          try {
            placeholderType = format && typeof format.type === "string" ? format.type : null;
          } catch {
            placeholderType = null;
          }
          return {
            layoutId,
            name: typeof shape.name === "string" ? shape.name : null,
            placeholderType,
            left: asNumber(shape.left),
            top: asNumber(shape.top),
            width: asNumber(shape.width),
            height: asNumber(shape.height),
          };
        });
      });
    });
  } catch {
    return null;
  }
}

/**
 * Sucht zu einem Platzhalter auf der Folie die passende Vorlage im Layout.
 *
 * Erster Weg ist die Platzhalterart, denn sie ist sprachunabhängig. Fehlt sie,
 * wird über den Formnamen zugeordnet – PowerPoint übernimmt den Namen aus dem
 * Layout, solange ihn niemand von Hand ändert.
 */
export function matchingLayoutShape(
  layoutId: string | null,
  placeholderType: string | null,
  shapeName: string | null,
  layoutShapes: LayoutShapeInfo[],
): LayoutShapeInfo | null {
  const imLayout = layoutShapes.filter((shape) => shape.layoutId === layoutId);
  if (imLayout.length === 0) return null;

  if (placeholderType) {
    const nachArt = imLayout.filter((shape) => shape.placeholderType === placeholderType);
    if (nachArt.length === 1) return nachArt[0];
  }
  if (shapeName) {
    const nachName = imLayout.filter((shape) => shape.name === shapeName);
    if (nachName.length === 1) return nachName[0];
  }
  return null;
}

/**
 * Weicht die Form deutlich von ihrer Sollposition im Layout ab?
 *
 * Die Schwelle von 6 pt entspricht gut zwei Millimetern. Kleinere Abweichungen
 * entstehen beim Skalieren zwischen Bildschirmgrößen und sind kein Hinweis auf
 * eine Verschiebung von Hand.
 */
export function isMovedFromLayout(
  shape: { left: number | null; top: number | null; width: number | null; height: number | null },
  layoutShape: LayoutShapeInfo,
  toleranz = 6,
): boolean {
  const paare: Array<[number | null, number | null]> = [
    [shape.left, layoutShape.left],
    [shape.top, layoutShape.top],
    [shape.width, layoutShape.width],
    [shape.height, layoutShape.height],
  ];
  return paare.some(
    ([ist, soll]) => ist !== null && soll !== null && Math.abs(ist - soll) > toleranz,
  );
}

export interface SlideBackgroundInfo {
  slideIndex: number;
  /** "Solid", "Gradient", "PictureOrTexture", "Pattern", "Unsupported". */
  fillType: string | null;
  /** Hintergrundfarbe, sofern es eine einfarbige Füllung ist. */
  color: string | null;
  /** Folgt die Folie dem Hintergrund ihres Folienmasters? */
  followsMaster: boolean | null;
}

/**
 * Hintergrund jeder Folie.
 * `null`, wenn PowerPointApi 1.10 fehlt – dann ist der Hintergrund nicht lesbar.
 */
export async function readSlideBackgrounds(): Promise<SlideBackgroundInfo[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.design)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      const backgrounds = slides.items.map((slide) => {
        const background = slide.background;
        background.load("isMasterBackgroundFollowed");
        background.fill.load("type");
        const solid = background.fill.getSolidFillOrNullObject();
        solid.load("color,isNullObject");
        return { background, solid };
      });
      await context.sync();

      return backgrounds.map((entry, index) => ({
        slideIndex: index,
        fillType: typeof entry.background.fill.type === "string" ? entry.background.fill.type : null,
        color:
          entry.solid.isNullObject !== true && typeof entry.solid.color === "string"
            ? entry.solid.color
            : null,
        followsMaster:
          typeof entry.background.isMasterBackgroundFollowed === "boolean"
            ? entry.background.isMasterBackgroundFollowed
            : null,
      }));
    });
  } catch {
    return null;
  }
}

/** Die Farben eines Designs, wie sie oben in jeder Farbpalette stehen. */
export type ThemeColorName =
  | "Accent1"
  | "Accent2"
  | "Accent3"
  | "Accent4"
  | "Accent5"
  | "Accent6"
  | "Dark1"
  | "Dark2"
  | "Light1"
  | "Light2"
  | "Hyperlink"
  | "FollowedHyperlink";

const THEME_COLORS: ThemeColorName[] = [
  "Accent1",
  "Accent2",
  "Accent3",
  "Accent4",
  "Accent5",
  "Accent6",
  "Dark1",
  "Dark2",
  "Light1",
  "Light2",
  "Hyperlink",
  "FollowedHyperlink",
];

/**
 * Farben des Designs als Hex-Werte.
 * `null`, wenn PowerPointApi 1.10 fehlt.
 */
export async function readThemeColors(): Promise<Record<ThemeColorName, string> | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.design)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      const first = slides.items[0];
      if (!first) return null;

      const results = THEME_COLORS.map((color) => first.themeColorScheme.getThemeColor(color));
      await context.sync();

      const colors = {} as Record<ThemeColorName, string>;
      THEME_COLORS.forEach((color, index) => {
        const raw = results[index].value;
        colors[color] = typeof raw === "string" ? raw : "";
      });
      return colors;
    });
  } catch {
    return null;
  }
}

export interface PageSetupInfo {
  /** Breite und Höhe einer Folie in Punkt. */
  slideWidth: number;
  slideHeight: number;
}

/**
 * Foliengröße der Präsentation.
 *
 * `null`, wenn PowerPointApi 1.10 fehlt. Für Prüfungen, die nur wissen müssen,
 * ob ein Objekt über den Folienrand hinausragt, gibt es `standardFolienmasse`
 * als Ersatzweg.
 */
export async function readPageSetup(): Promise<PageSetupInfo | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.design)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const pageSetup = context.presentation.pageSetup;
      pageSetup.load("slideWidth,slideHeight");
      await context.sync();

      const breite = pageSetup.slideWidth;
      const hoehe = pageSetup.slideHeight;
      if (typeof breite !== "number" || typeof hoehe !== "number") return null;
      return { slideWidth: breite, slideHeight: hoehe };
    });
  } catch {
    return null;
  }
}

/**
 * Foliengröße im Breitbildformat, wie PowerPoint sie seit 2013 voreinstellt:
 * 13,333 × 7,5 Zoll, in Punkt also 960 × 540.
 *
 * Ersatzweg, solange `readPageSetup` nichts liefert. Bei einer Präsentation im
 * älteren 4:3-Format (720 × 540) ist die Folie schmaler – ein Objekt, das über
 * 960 pt hinausragt, liegt aber in jedem Fall außerhalb.
 */
export const standardFolienmasse: PageSetupInfo = { slideWidth: 960, slideHeight: 540 };

export interface DocumentPropertiesInfo {
  title: string;
  author: string;
  company: string;
  subject: string;
  keywords: string;
}

/**
 * Dokumenteigenschaften der Präsentation.
 * `null`, wenn PowerPointApi 1.7 fehlt.
 */
export async function readDocumentProperties(): Promise<DocumentPropertiesInfo | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.documentProperties)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const properties = context.presentation.properties;
      properties.load("title,author,company,subject,keywords");
      await context.sync();

      const asText = (value: unknown) => (typeof value === "string" ? value.trim() : "");

      return {
        title: asText(properties.title),
        author: asText(properties.author),
        company: asText(properties.company),
        subject: asText(properties.subject),
        keywords: asText(properties.keywords),
      };
    });
  } catch {
    return null;
  }
}

/**
 * Layouts, die im Standard-Design von PowerPoint für Abschnittstrenner
 * gedacht sind. Der Layoutname ist sprachabhängig, deshalb wird zusätzlich
 * gegen den sprachunabhängigen Layouttyp geprüft, sobald er verfügbar ist.
 */
export function isSectionHeaderLayout(layoutType: string | null, layoutName: string | null): boolean {
  if (layoutType === "SectionHeader") return true;
  if (layoutType !== null) return false;
  return /abschnitt|section/i.test(layoutName ?? "");
}

/** Ist das Layout eine Titelfolie? */
export function isTitleLayout(layoutType: string | null, layoutName: string | null): boolean {
  if (layoutType === "Title") return true;
  if (layoutType !== null) return false;
  return /^(titelfolie|title slide|titel)$/i.test((layoutName ?? "").trim());
}

/**
 * Gehört der Farbwert zu den Farben des Designs?
 *
 * PowerPoint liefert für Schrift- und Füllfarben immer den aufgelösten
 * Hex-Wert – ob jemand die Farbe aus der Designpalette oder aus den
 * Standardfarben gewählt hat, steht nicht dabei. Über den Vergleich mit dem
 * Farbschema lässt es sich trotzdem beantworten: Was im Schema vorkommt, kam
 * mit hoher Wahrscheinlichkeit auch von dort.
 */
export function isThemeColor(
  value: string | null,
  theme: Record<ThemeColorName, string> | null,
): boolean {
  if (!value || !theme) return false;
  const normiert = value.trim().replace("#", "").toUpperCase();
  return Object.values(theme).some(
    (farbe) => farbe.trim().replace("#", "").toUpperCase() === normiert,
  );
}
